import styled from '@emotion/styled';
import { Box, CardContent, Chip, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ClosingStatus, RemoteResource } from '../../types';
import { EmptyState } from '../Visual';
import { BaseCard } from '../base';

interface DashboardInsightsProps {
  salesResource?: RemoteResource<unknown[]>;
  collectionsResource?: RemoteResource<unknown[]>;
  dailySalesResource?: RemoteResource<unknown[]>;
}

interface SummaryData {
  ok: number;
  error: number;
  total: number;
  okPercent: number;
  errorPercent: number;
  countries: {
    country: string;
    ok: number;
    error: number;
    total: number;
  }[];
}

const InsightCard = styled(BaseCard)`
  height: 100%;
`;

const InsightCardContent = styled(CardContent)`
  height: 100%;
  padding: ${({ theme }) => theme.spacing(2.25)};

  &:last-child {
    padding-bottom: ${({ theme }) => theme.spacing(2.25)};
  }
`;

const CardHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const ChartWrapper = styled(Box)`
  height: 150px;
  min-width: 0;
`;

const Metric = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const isClosingStatus = (item: unknown): item is ClosingStatus => {
  if (!item || typeof item !== 'object') return false;
  return 'closeOk' in item && 'closeError' in item && 'country' in item;
};

const getSummary = (resource?: RemoteResource<unknown[]>): SummaryData => {
  const data = resource?.data?.filter(isClosingStatus) || [];
  const ok = data.reduce((acc, item) => acc + item.closeOk, 0);
  const error = data.reduce((acc, item) => acc + item.closeError, 0);
  const total = ok + error;
  const okPercent = total ? Math.round((ok * 10000) / total) / 100 : 0;
  const errorPercent = total ? Math.round((error * 10000) / total) / 100 : 0;
  const countries = data
    .map(item => ({
      country: item.country,
      ok: item.closeOk,
      error: item.closeError,
      total: item.closeOk + item.closeError,
    }))
    .sort((a, b) => b.error - a.error)
    .slice(0, 6);

  return { ok, error, total, okPercent, errorPercent, countries };
};

const ChartSkeleton = () => (
  <Box>
    <Skeleton height={30} />
    <Skeleton variant="rounded" height={128} />
  </Box>
);

const ChartEmptyState = ({ title, description }: { title: string; description: string }) => (
  <EmptyState title={title} description={description} />
);

interface DonutInsightProps {
  title: string;
  summary: SummaryData;
  loading?: boolean;
}

const DonutInsight = ({ title, summary, loading }: DonutInsightProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartData = [
    { name: t('dashboard.insights.ok', { defaultValue: 'OK' }), value: summary.ok },
    { name: t('dashboard.insights.missings', { defaultValue: 'Faltantes' }), value: summary.error },
  ];
  const colors = [theme.palette.success.main, theme.palette.error.main];

  const renderChart = () => {
    if (loading) return <ChartSkeleton />;
    if (summary.total === 0)
      return (
        <ChartEmptyState
          title={t('dashboard.insights.empty.chart')}
          description={t('dashboard.insights.empty.description')}
        />
      );

    return (
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              innerRadius={42}
              outerRadius={62}
              dataKey="value"
              paddingAngle={4}
              stroke="none"
            >
              {chartData.map(item => (
                <Cell key={item.name} fill={colors[chartData.indexOf(item)]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </ChartWrapper>
    );
  };

  return (
    <InsightCard>
      <InsightCardContent>
        <CardHeader>
          <Metric>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="h5">{summary.okPercent}%</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.insights.totalRecords', { total: summary.total })}
            </Typography>
          </Metric>
          <Chip
            size="small"
            color={summary.error > 0 ? 'warning' : 'success'}
            label={
              summary.error > 0
                ? t('dashboard.insights.pending', { count: summary.error })
                : t('dashboard.insights.noPending')
            }
          />
        </CardHeader>
        {renderChart()}
      </InsightCardContent>
    </InsightCard>
  );
};

interface CountryErrorBarProps {
  salesSummary: SummaryData;
  collectionsSummary: SummaryData;
  loading?: boolean;
}

const CountryErrorBar = ({ salesSummary, collectionsSummary, loading }: CountryErrorBarProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const axisTick = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    fill: theme.palette.text.secondary,
  };
  const data = useMemo(() => {
    const map = new Map<string, { country: string; sales: number; collections: number }>();
    salesSummary.countries.forEach(item => {
      map.set(item.country, { country: item.country, sales: item.error, collections: 0 });
    });
    collectionsSummary.countries.forEach(item => {
      const current = map.get(item.country) || { country: item.country, sales: 0, collections: 0 };
      map.set(item.country, { ...current, collections: item.error });
    });
    return Array.from(map.values())
      .sort((a, b) => b.sales + b.collections - (a.sales + a.collections))
      .slice(0, 6);
  }, [collectionsSummary, salesSummary]);

  const renderChart = () => {
    if (loading) return <ChartSkeleton />;
    if (data.length === 0)
      return (
        <ChartEmptyState
          title={t('dashboard.insights.empty.byCountry')}
          description={t('dashboard.insights.empty.description')}
        />
      );

    return (
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <XAxis dataKey="country" tick={axisTick} />
            <YAxis allowDecimals={false} tick={axisTick} />
            <Tooltip />
            <Bar
              dataKey="sales"
              name={t('dashboard.insights.sales')}
              fill={theme.palette.primary.main}
              radius={[8, 8, 0, 0]}
            />
            <Bar
              dataKey="collections"
              name={t('dashboard.insights.collections')}
              fill={theme.palette.secondary.main}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
    );
  };

  return (
    <InsightCard>
      <InsightCardContent>
        <CardHeader>
          <Metric>
            <Typography variant="subtitle2">
              {t('dashboard.insights.countriesWithPending')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.incorporation.titles.missingList')}
            </Typography>
          </Metric>
          <Chip size="small" label={t('dashboard.insights.topCount', { count: 6 })} />
        </CardHeader>
        {renderChart()}
      </InsightCardContent>
    </InsightCard>
  );
};

const DailySalesInsight = ({ resource }: { resource?: RemoteResource<unknown[]> }) => {
  const { t } = useTranslation();
  const total = resource?.data?.length || 0;
  const emptyContentHeight = 150;

  const renderContent = () => {
    if (resource?.loading) return <ChartSkeleton />;
    if (total === 0)
      return (
        <Box
          sx={{
            height: emptyContentHeight,
          }}
        />
      );

    return (
      <Box
        sx={theme => ({
          borderRadius: 3,
          height: emptyContentHeight,
          background: theme.customTokens.surfaces.panelGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
        })}
      >
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.insights.dailySales.note')}
        </Typography>
      </Box>
    );
  };

  return (
    <InsightCard>
      <InsightCardContent>
        <CardHeader>
          <Metric>
            <Typography variant="subtitle2">{t('dashboard.insights.dailySales.title')}</Typography>
            <Typography variant="h5">{resource?.loading ? '-' : total}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.insights.dailySales.recordsToday')}
            </Typography>
          </Metric>
          <Chip
            size="small"
            color={resource?.loading ? 'default' : 'info'}
            label={
              resource?.loading
                ? t('dashboard.insights.status.loading')
                : t('dashboard.insights.status.online')
            }
          />
        </CardHeader>
        {renderContent()}
      </InsightCardContent>
    </InsightCard>
  );
};

const DashboardInsights = ({
  salesResource,
  collectionsResource,
  dailySalesResource,
}: DashboardInsightsProps) => {
  const { t } = useTranslation();
  const salesSummary = useMemo(() => getSummary(salesResource), [salesResource]);
  const collectionsSummary = useMemo(() => getSummary(collectionsResource), [collectionsResource]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} lg={3} data-tour="dashboard-insights-summary">
        <DonutInsight
          title={t('dashboard.insights.sales')}
          summary={salesSummary}
          loading={salesResource?.loading}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <DonutInsight
          title={t('dashboard.insights.collections')}
          summary={collectionsSummary}
          loading={collectionsResource?.loading}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <CountryErrorBar
          salesSummary={salesSummary}
          collectionsSummary={collectionsSummary}
          loading={salesResource?.loading || collectionsResource?.loading}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <DailySalesInsight resource={dailySalesResource} />
      </Grid>
    </Grid>
  );
};

export default DashboardInsights;
